'use memo';

import type React from 'react';
import type { IconName } from '@/components/Icon/iconMap';
import { OrpcAttributeRenderer, type AttributeRenderInterceptor, type BaseProps, type ProvisionalValueTypeRenderer, type ValueTypeOptions } from './OrpcAttributeRenderer';
import type { Attribute, StoreValues, UpdateSectionAttribute, Value } from './type';

/**
 * OpenAPI（/v2/schema/section）互換レイヤー
 *
 * 描画ロジックはすべて OrpcAttributeRenderer.tsx にあり、本ファイルは
 * OpenAPI 生成型で型付けした薄いラッパーと互換型のみを持つ。
 * oRPC 移行が完了して OpenAPI が廃止されたら、type.ts と共に削除する。
 *
 * 本ファイル内の `as` キャストはすべて「共通型（OrpcAttribute 等）→
 * OpenAPI 生成型」の変換。このラッパーに渡す attributes / storeValues の
 * 実体が OpenAPI 生成型であるため、コールバックへ戻ってくる値を元の型と
 * して扱うのは実行時に安全（順方向の代入可能性は
 * __test__/orpcAttributeRenderer.test.ts で検証）。
 */

/** BaseProps は共通定義（OrpcAttributeRenderer.tsx）を再エクスポート */
export type { BaseProps } from './OrpcAttributeRenderer';

/** ProvisionalValueTypeRenderer の OpenAPI 生成型版（利用側の型注釈向け） */
export type OpenApiProvisionalValueTypeRenderer = (attribute: Attribute, baseProps: BaseProps, getCurrentAttribute: (attributeKey: string, sectionKey: string, storeValues: StoreValues) => Attribute | undefined) => React.ReactElement | null;

/** AttributeRenderInterceptorContext の OpenAPI 生成型版 */
export type OpenApiAttributeRenderInterceptorContext = {
  sectionKey: string;
  attr: Attribute;
  baseProps: BaseProps;
  handleValueChange: (newValue: unknown) => void;
  updateSectionAttribute: UpdateSectionAttribute;
  storeValues: StoreValues;
};

/** AttributeRenderInterceptor の OpenAPI 生成型版（利用側の型注釈向け） */
export type OpenApiAttributeRenderInterceptor = (context: OpenApiAttributeRenderInterceptorContext) => React.ReactElement | null;
interface Props {
  attributes: Attribute[];
  sectionKey: string;
  updateSectionAttribute: UpdateSectionAttribute;
  storeValues: StoreValues;
  /** valueType固有のオプション */
  options?: ValueTypeOptions;
  /** ValueType=CUSTOMを設定した項目に対して、ドメイン独自のレンダリングを実施する場合に利用する */
  provisionalValueTypeRenderer?: OpenApiProvisionalValueTypeRenderer;
  /**
   * valueType の分岐の前に呼ばれる。非 null を返すと既定のレンダリングをスキップする。
   */
  attributeRenderInterceptor?: OpenApiAttributeRenderInterceptor;
  /**
   * BADGE 属性のアイコンを解決する（ドメイン固有の見た目を注入する）。
   * 引数は BE の `BadgeValue.code`。値自体が icon を持つ場合はそちらを優先する。
   */
  badgeIcon?: (code: string) => IconName | undefined;
}

/**
 * 汎用属性レンダラー（OpenAPI スキーマ型版）
 *
 * バックエンドのvalueTypeに基づいて動的にフォームコンポーネントを生成します。
 * 描画ロジックは OrpcAttributeRenderer と共通で、本コンポーネントは
 * OpenAPI 生成型（/v2/schema/section）で型付けした薄いラッパー。
 *
 * ## 使用例
 * ```tsx
 * <OpenApiAttributeRenderer
 *   attributes={section.attributes}
 *   sectionKey={section.sectionKey}
 *   actions={store.actions}
 *   storeValues={store.values}
 * />
 * ```
 */
export function OpenApiAttributeRenderer({
  attributes,
  sectionKey,
  updateSectionAttribute,
  storeValues,
  provisionalValueTypeRenderer,
  attributeRenderInterceptor,
  options,
  badgeIcon
}: Props) {
  // 共通型で渡ってくる属性・ストアを OpenAPI 生成型として利用側へ戻す
  const adaptedProvisionalRenderer: ProvisionalValueTypeRenderer | undefined = provisionalValueTypeRenderer && ((attribute, baseProps, getCurrentAttribute) => provisionalValueTypeRenderer(attribute as Attribute, baseProps, (attributeKey, targetSectionKey, targetStoreValues) => getCurrentAttribute(attributeKey, targetSectionKey, targetStoreValues) as Attribute | undefined));
  const adaptedInterceptor: AttributeRenderInterceptor | undefined = attributeRenderInterceptor && (context => attributeRenderInterceptor({
    ...context,
    attr: context.attr as Attribute,
    storeValues: context.storeValues as StoreValues
  }));
  return <OrpcAttributeRenderer attributes={attributes} sectionKey={sectionKey}
  // CForm コンポーネント群の onChange 出力は API スキーマの Value と
  // 構造的に互換（従来 handleValueChange 内で行っていたキャストを
  // OpenAPI 境界へ移動したもの）。displayValue 未指定時は引数ごと省略し、
  // 呼び出しの引数個数を従来と一致させる
  updateSectionAttribute={(targetSectionKey, attributeKey, value, displayValue) => displayValue === undefined ? updateSectionAttribute(targetSectionKey, attributeKey, value as Value) : updateSectionAttribute(targetSectionKey, attributeKey, value as Value, displayValue)} storeValues={storeValues} provisionalValueTypeRenderer={adaptedProvisionalRenderer} attributeRenderInterceptor={adaptedInterceptor} options={options} badgeIcon={badgeIcon} />;
}